<template>
  <div v-if="isOpen">
    <v-dialog 
      v-model="isOpen" 
      persistent
      :max-width="parsedResult ? '1200px' : '600px'"
      max-height="auto"
    >
      <v-card>
        <v-card-title v-if="!parsedResult">
          <span class="headline">Import Members</span>
        </v-card-title>
       
        <v-card-text class="px-6 pb-0 pt-6">
          <div class="mb-4 text-body-2 text--secondary" v-if="!parsedResult">
            Upload a CSV file with one entry per row and fields in the following order:<br />
            <strong>Email Address, Password, First Name, Last Name, Company Name, Client Name, Street Address, City, State/Province, Division, Zip Code, Phone, Fax</strong>
          </div>
          <div class="d-flex align-center" style="gap: 10px; max-width: 600px;">
            <v-file-input
              label="Select CSV file*"
              outlined
              dense
              v-model="selectedFile"
              accept=".csv"
              style="max-width: 400px;"
            />

            <v-btn v-if="parsedResult" small color="primary" @click="handleImportCsvParse" class="mb-6">
              Import
            </v-btn>
          </div>
        </v-card-text>

        <div class="mx-6" v-if="parsedResult">
          <v-data-table
            elevation="1"
            class="mb-4"
            :loading="isLoading"
            :headers="headers"
            :items="parsedResult.data"
            :page.sync="page"
            :options.sync="options"
            :server-items-length="totRecords"
            :item-class="rowClass"
            hide-default-footer
            item-key="uniqueKey"
          >
            <template v-slot:item="{ item }">
              <tr :class="item.status === 'declined' ? 'row-declined' : ''">
                <td>{{ item.sNo }}</td>
                <td>{{ item.emailAddress }}</td>
                <td>{{ item.password }}</td>
                <td>{{ item.firstName }}</td>
                <td>{{ item.lastName }}</td>
                <td>{{ item.companyName }}</td>
                <td>{{ item.clientName }}</td>
                <td>{{ item.streetAddress }}</td>
                <td>{{ item.city }}</td>
                <td>{{ item.state }}</td>
                <td>{{ item.country }}</td>
                <td>{{ item.zipCode }}</td>
                <td>{{ item.phone }}</td>
                <td>{{ item.fax }}</td>
                <td>{{ item.interestArea }}</td>
                <td>{{ item.active }}</td>
                <td>{{ item.status }}</td>
              </tr>
            </template>
          </v-data-table>
          <div class="text-center pb-4">
            <Pagination
              :isloading="isLoading"
              :startRecord="startRecord"
              :currentPage="currentPage"
              :lastPage="lastPage"
              :lastRecord="lastRecord"
              :totRecords="totRecords"
              :isCurrentPageClass="isCurrentPageClass"
              :perpage="perpage"
              :getLastPageClass="getLastPageClass"
              :totPage="totPage"
              :getPages="getPages"
              @handlePerPage="handlePerPage"
              @paginate="paginate"
              @last="last"
              @getDataByPage="getDataByPage"
              :showPerPage="true"
            />
          </div>
          <div class="d-flex align-center" style="font-size: 12px;">
            <span>Total Users: {{ parsedResult.total_records || 0 }}</span>
            <span class="mx-2">|</span>

            <span>Valid Users: {{ parsedResult.valid_users || 0 }}</span>
            <span class="mx-2">|</span>

            <span>Skipped Users: {{ parsedResult.skipped_users || 0 }}</span>
          </div>

        </div>

        <v-card-actions class="justify-end px-6 pb-4">
          <v-spacer />
          <v-btn v-if="!parsedResult" small color="primary" @click="handleImportCsvParse">Import</v-btn>
          <v-btn
            v-else
            small
            color="primary"
            :disabled="!parsedResult.valid_users"
            @click="handleAcceptOrDecline(true)"
          >
            Accepted All (Import Users)
          </v-btn>
          <v-btn v-if="!parsedResult" small color="grey" @click="handleCancel">Cancel</v-btn>
          <v-btn v-else small color="grey" @click="handleAcceptOrDecline(false)">Decline All</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import Pagination from "../../component/Pagination.vue";

export default {
  name: "MemberImportDialog",
  components: { Loading, Pagination },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      selectedFile: null,
      errorMsg: "",
      parsedResult: null,

      options: {},
      isLoading: false,
      page: 1,
      pageCount: 0,
      perpage: 10,
      totRecords: 0,

      headers: [
        {
          text: "S No",
          value: "sNo",
          sortable: false,
          width: "80",
          class: "v-data-table-header",
        },
        {
          text: "Email Address",
          value: "emailAddress",
          sortable: false,
          width: "150",
          class: "v-data-table-header",
        },
        {
          text: "Password",
          value: "password",
          sortable: false,
          width: "80",
          class: "v-data-table-header",
        },
        {
          text: "First Name",
          value: "firstName",
          sortable: false,
          width: "120",
          class: "v-data-table-header",
        },
        {
          text: "Last Name",
          value: "lastName",
          sortable: false,
          width: "100",
          class: "v-data-table-header",
        },
        {
          text: "Company Name",
          value: "companyName",
          sortable: false,
          width: "150",
          class: "v-data-table-header",
        },
        {
          text: "Client Name",
          value: "clientName",
          sortable: false,
          width: "120",
          class: "v-data-table-header",
        },
        {
          text: "Street Address",
          value: "streetAddress",
          sortable: false,
          width: "150",
          class: "v-data-table-header",
        },
        {
          text: "City",
          value: "city",
          sortable: false,
          width: "120",
          class: "v-data-table-header",
        },
        {
          text: "State/Province",
          value: "state",
          sortable: false,
          width: "120",
          class: "v-data-table-header",
        },
        {
          text: "Division",
          value: "country",
          sortable: false,
          width: "100",
          class: "v-data-table-header",
        },
        {
          text: "Zip/Postal Code",
          value: "zipCode",
          sortable: false,
          width: "150",
          class: "v-data-table-header",
        },
        {
          text: "Phone",
          value: "phone",
          sortable: false,
          width: "150",
          class: "v-data-table-header",
        },
        {
          text: "Fax",
          value: "fax",
          sortable: false,
          width: "100",
          class: "v-data-table-header",
        },
        {
          text: "Area of Interest",
          value: "interestArea",
          sortable: false,
          width: "100",
          class: "v-data-table-header",
        },
        {
          text: "Active",
          value: "active",
          sortable: false,
          width: "60",
          class: "v-data-table-header",
        },
        {
          text: "Status",
          value: "status",
          sortable: false,
          width: "60",
          class: "v-data-table-header",
        },
      ],
    };
  },
  computed: {
    //pagination computed methods
    startRecord: function () {
      return parseInt(this.lastRecord) - parseInt(this.perpage) + parseInt(1);
    },
    lastRecord: function () {
      return this.perpage * this.currentPage;
    },
    isCurrentPageClass: function () {
      return this.currentPage === 1 ? "" : "notactivespan";
    },
    getLastPageClass: function () {
      return parseInt(this.lastPage) === parseInt(this.currentPage)
        ? ""
        : "notactivespan";
    },
    totPage: function () {
      return Math.ceil(parseInt(this.totRecords) / parseInt(this.perpage));
    },
    getPages: function () {
      return this.$utils.getPages(
        this.lastPage,
        this.currentPage,
        this.totPage
      );
    },
    currentPage: function () {
      return this.page;
    },
    lastPage: function () {
      return Math.ceil(parseInt(this.totRecords) / parseInt(this.perpage));
    },
    endIndex: function () {
      return this.perpage * this.page;
    },
    startIndex: function () {
      if (this.page == 1) {
        return this.perpage * 0;
      } else {
        return this.endIndex - this.perpage;
      }
    },
  },
  watch: {
    isOpen(val) {
      if(!val){
        this.selectedFile = null;
      }
    },
    isLoading(val) {
			this.$emit("loading", val);
    },
    options: {
      handler() {
        requestAnimationFrame(()=>{
          this.fetchPaginatedRecords();
        })
      },
      deep: true,
    }
  },
  methods: {
     //pagination methods
    getDataByPage(value) {
      this.page = value;
    },
    handlePerPage(value) {
      if(this.perpage===value){
        return;
      }
      this.perpage = value;
      this.page = 1;
      this.fetchPaginatedRecords()
    },
    paginate(n) {
      if(n==0){
        // First page
        this.page = 1
      }else{
        const nextPage = this.page + n;
        this.page = Math.min(Math.max(1, nextPage), this.pageCount)
      }
    },
    last() {
      this.page = this.lastPage;
    },
    rowClass(item) {
      if(item.status === 'declined') {
        return 'row-declined'
      }
      return '' // default
    },
    handleCancel() {
      this.selectedFile = null;
      this.$emit("close");
    },
    async handleImportCsvParse(){
      const file = this.selectedFile
      if(file){
        this.isLoading = true;
        try {
          let formData = new FormData();
          formData.append('file', file);
          let membersImportCsvParseUrl = this.$url('MEMBER_IMPORT_CSV_PARSE')
          const response = await this.$fetch({ requiresAuth: true, operation : 'add' , vueScope: this })
            .post(membersImportCsvParseUrl, formData, {
              headers: { 
                'Content-Type': 'multipart/form-data'
              }
            })
          if ([200, 201].includes(response.data?.statusCode)) {
            this.parsedResult = response.data?.payload
            if(this.parsedResult?.data) {
              this.parsedResult.data = this.parsedResult.data.map((e, i)=>({
                ...e,
                sNo: i+1,
                active: e.active === "n" ? "No" : "Yes",
                key: `${i+1}-${e.emailAddress}`
              }))
              const page_size = response.data?.payload?.page_size || 10;
              this.totRecords = response.data?.payload?.total_records || 0;
              this.pageCount = Math.floor((this.totRecords + page_size - 1)/ page_size);
            }
          } else {
            throw new Error(response?.data?.payload?.message || 'Something went wrong')
          }
        } catch (error) {
          console.error(error);
          let errMsg = error.response?.data?.payload?.message || "Something went wrong!"
          this.$swal.fire({
            icon: "error",
            title: "Error",
            text: errMsg,
          });
        } finally {
          this.isLoading = false;
        }
      }
    },
    async handleAcceptOrDecline(accept){
      this.isLoading = true;
      try {
        let membersImportUrl = this.$url('MEMBER_IMPORT_CSV')
        const response = await this.$fetch({ requiresAuth: true, operation : 'add' , vueScope: this })
          .post(membersImportUrl, {
            "status": accept ? "accepted" : "declined",
          })
        if ([200, 201].includes(response.data?.statusCode)) {
          this.parsedResult = null
          // this.showMemberImportDialog = false;
          this.$swal.fire({
            icon: "success",
            title: "Success",
            text: response.data?.payload?.message,
          });
          this.$emit("success", accept);
        }else{
          throw new Error(response?.data?.payload?.message || 'Something went wrong')
        }
      } catch (error) {
        console.error(error);
        let errMsg = error.response?.data?.payload?.message || "Something went wrong!"
        this.$swal.fire({
          icon: "error",
          title: "Error",
          text: errMsg,
        });
      } finally {
        this.isLoading = false;
      }
    },

    async fetchPaginatedRecords() {
      this.isLoading = true;
      try {
        let csvPaginationUrl = this.$url("MEMBER_IMPORT_CSV_PAGINATION");
        const response = await this.$fetch({
          requiresAuth: true,
          operation: "view",
          vueScope: this,
        }).get(csvPaginationUrl, { params: { page_number: this.page, page_size: this.perpage } });
        // this.items = response.data?.payload?.users?.map(item=>({
        //   ...item,
        //   active: item.active === 'y' ? 'Yes' : 'No'
        // }));
        // this.parsedResult.data = this.parsedResult.data.map((e, i)=>({
        //   ...e,
        //   sNo: i+1,
        //   active: e.active === "n" ? "No" : "Yes",
        //   key: `${i+1}-${e.emailAddress}`
        // }))
        // const page_size = response.data?.payload?.page_size || 10;
        // this.totRecords = response.data?.payload?.total_records || 0;
        // this.pageCount = this.totRecords / page_size;

        if ([200, 201].includes(response.data?.statusCode)) {
          this.parsedResult = response.data?.payload
          if(this.parsedResult?.data) {
            this.parsedResult.data = this.parsedResult.data.map((e, i)=>({
              ...e,
              sNo: i+1,
              key: `${i+1}-${e.emailAddress}`
            }))
            const page_size = response.data?.payload?.page_size || 10;
            this.totRecords = response.data?.payload?.total_records || 0;
            this.pageCount = Math.floor((this.totRecords + page_size - 1)/ page_size);
          }
        }
      } catch (error) {
        this.items = []
        this.totRecords = 0
        this.$swal.fire({
          icon: "error",
          title: "Error",
          text: "No results found.",
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
<style>
.row-declined td{
  background-color: #ffa0a0 !important;
}
</style>
